import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OutboxOutlinedIcon from "@mui/icons-material/OutboxOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { format as formatDateFn } from "date-fns";
import { useTranslation } from "react-i18next";
import { dateTimeFormat } from "../../../common/helpers/formatDateTime";
import {
  TesSalaryTableDocument,
  TesSalaryTablesGroupedBySeriesResult,
} from "../../tesSalaryTables/tesSalaryTablesApi";
import { LEGACY_SERIES_ID } from "./useTesSalaryTablesAdminData";

const getReadableTableLabel = ({
  name,
  effectiveDate,
}: {
  name?: string | null;
  effectiveDate?: string | null;
}) => {
  if (!name) return null;
  return effectiveDate ? `${name} (${effectiveDate})` : name;
};

const sortTablesByEffectiveDateDesc = (tables: TesSalaryTableDocument[]) =>
  [...tables].sort((a, b) => {
    const effectiveDateComparison = (b.effectiveDate || "").localeCompare(
      a.effectiveDate || "",
    );
    if (effectiveDateComparison !== 0) return effectiveDateComparison;
    return (b.updatedAt || 0) - (a.updatedAt || 0);
  });

type Props = {
  group: TesSalaryTablesGroupedBySeriesResult;
  localizedWorkTitles: Record<string, string>;
  onOpenTable: (
    mode: "create" | "edit" | "copy" | "view",
    table?: TesSalaryTableDocument,
    seriesId?: string,
  ) => void;
  onOpenSeriesEdit: (
    series: TesSalaryTablesGroupedBySeriesResult["series"],
  ) => void;
  onPublish: (table: TesSalaryTableDocument) => void;
  onArchive: (table: TesSalaryTableDocument) => void;
  onDelete: (table: TesSalaryTableDocument) => void;
};

const TesSalaryTableSeriesCard = ({
  group,
  localizedWorkTitles,
  onOpenTable,
  onOpenSeriesEdit,
  onPublish,
  onArchive,
  onDelete,
}: Props) => {
  const { t } = useTranslation();

  const formatUpdatedAt = (updatedAt?: number) => {
    if (!updatedAt) return "-";
    try {
      return formatDateFn(new Date(updatedAt), dateTimeFormat);
    } catch {
      return "-";
    }
  };

  const publishLabel = (tableStatus: string): string => {
    if (tableStatus === "PUBLISHING") {
      return t("settings:tesSalaryTablesPublishing");
    }
    if (tableStatus === "PUBLISHED") {
      return t("settings:tesSalaryTablesPublishAgain");
    }
    return t("settings:tesSalaryTablesPublish");
  };

  const publishColor = (tableStatus: string) => {
    if (tableStatus === "PUBLISHED") {
      return "secondary" as const;
    } else {
      return "primary" as const;
    }
  };
  const renderTableSection = (
    title: string,
    tables: TesSalaryTableDocument[],
    emptyLabel: string,
  ) => (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginTop={2}
        marginBottom={1}
      >
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {tables.length}
        </Typography>
      </Box>
      {tables.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {emptyLabel}
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("name")}</TableCell>
                <TableCell>
                  {t("settings:tesSalaryTablesEffectiveDate")}
                </TableCell>
                <TableCell>{t("status")}</TableCell>
                <TableCell>{t("hourEntries:modified")}</TableCell>
                <TableCell>{t("hourEntries:modifiedByShort")}</TableCell>
                <TableCell>{t("workTitles")}</TableCell>
                <TableCell align="right">{t("settings:actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tables.map((table) => {
                const sourceLabel = getReadableTableLabel({
                  name: table.sourceTableName,
                  effectiveDate: table.sourceTableEffectiveDate,
                });

                return (
                  <TableRow key={table.tesSalaryTableId}>
                    <TableCell>
                      <Box>
                        <Typography>{table.name}</Typography>
                        {sourceLabel && (
                          <Typography variant="caption" color="text.secondary">
                            {`${t("settings:tesSalaryTablesBasedOn")} ${sourceLabel}`}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{table.effectiveDate}</TableCell>
                    <TableCell>
                      {t(`settings:tesSalaryTablesStatus${table.status}`)}
                    </TableCell>
                    <TableCell>{formatUpdatedAt(table.updatedAt)}</TableCell>
                    <TableCell>
                      {table.updatedByName || table.updatedBy || "-"}
                    </TableCell>
                    <TableCell>
                      {(table.workTitleIds || []).length
                        ? (table.workTitleIds || [])
                            .map((id) => localizedWorkTitles[id] || id)
                            .join(", ")
                        : t("talentProfile:allWorkTitles")}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        {table.status !== "DRAFT" && (
                          <Tooltip title={t("common:open")}>
                            <IconButton
                              size="small"
                              aria-label={t("common:open")}
                              onClick={() => onOpenTable("view", table)}
                            >
                              <OpenInNewOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {table.status === "DRAFT" && (
                          <Tooltip title={t("edit")}>
                            <IconButton
                              size="small"
                              aria-label={t("edit")}
                              onClick={() => onOpenTable("edit", table)}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!table.successorTableId && (
                          <Tooltip title={t("common:duplicate")}>
                            <IconButton
                              size="small"
                              aria-label={t("common:duplicate")}
                              onClick={() => onOpenTable("copy", table)}
                            >
                              <ContentCopyOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(table.status === "DRAFT" ||
                          table.status === "PUBLISHING" ||
                          (table.status === "PUBLISHED" &&
                            !!table.sourceTableId)) && (
                          <Tooltip title={publishLabel(table.status)}>
                            <IconButton
                              size="small"
                              color={publishColor(table.status)}
                              aria-label={publishLabel(table.status)}
                              onClick={() => onPublish(table)}
                            >
                              <OutboxOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {table.status === "PUBLISHED" && (
                          <Tooltip title={t("settings:tesSalaryTablesArchive")}>
                            <IconButton
                              size="small"
                              color="warning"
                              aria-label={t("settings:tesSalaryTablesArchive")}
                              onClick={() => onArchive(table)}
                            >
                              <ArchiveOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {table.status === "DRAFT" && (
                          <Tooltip title={t("delete")}>
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={t("delete")}
                              onClick={() => onDelete(table)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  const publishedTables = sortTablesByEffectiveDateDesc(
    group.tables.filter((table) => table.status === "PUBLISHED"),
  );
  const draftTables = sortTablesByEffectiveDateDesc(
    group.tables.filter(
      (table) => table.status === "DRAFT" || table.status === "PUBLISHING",
    ),
  );
  const otherTables = sortTablesByEffectiveDateDesc(
    group.tables.filter(
      (table) =>
        table.status !== "PUBLISHED" &&
        table.status !== "DRAFT" &&
        table.status !== "PUBLISHING",
    ),
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        display="flex"
        alignItems="baseline"
        justifyContent="space-between"
        marginBottom={1}
        gap={2}
        flexWrap="wrap"
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h6">{group.series.name}</Typography>
            {group.series.tesSalaryTableSeriesId !== LEGACY_SERIES_ID && (
              <Button
                size="small"
                variant="text"
                aria-label={`Edit series ${group.series.name}`}
                onClick={() => onOpenSeriesEdit(group.series)}
              >
                {t("edit")}
              </Button>
            )}
          </Box>
          {group.series.description && (
            <Typography variant="caption" color="text.secondary">
              {group.series.description}
            </Typography>
          )}
        </Box>
        {group.series.tesSalaryTableSeriesId !== LEGACY_SERIES_ID && group.tables.length === 0 && (
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              onOpenTable(
                "create",
                undefined,
                group.series.tesSalaryTableSeriesId,
              )
            }
          >
            {t("settings:tesSalaryTablesCreateDraftShort")}
          </Button>
        )}
        <Typography variant="caption" color="text.secondary">
          {`${t("hourEntries:modified")}: ${formatUpdatedAt(group.series.updatedAt)}`}
        </Typography>
      </Box>
      {renderTableSection(
        t("settings:tesSalaryTablesStatusPUBLISHED"),
        publishedTables,
        t("settings:tesSalaryTablesNoPublishedTables"),
      )}
      {renderTableSection(
        t("settings:tesSalaryTablesStatusDRAFT"),
        draftTables,
        t("settings:tesSalaryTablesNoDraftTables"),
      )}
      {otherTables.length > 0 &&
        renderTableSection(
          t("common:other"),
          otherTables,
          t("settings:tesSalaryTablesNoOtherTables"),
        )}
    </Paper>
  );
};

export default TesSalaryTableSeriesCard;
